import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  AppLocale,
  DEFAULT_LOCALE,
  LOCALE_REGISTRY,
  PSEUDO_LOCALE,
  PRODUCTION_LOCALES,
  SOURCE_LOCALE,
  resolveSupportedLocale,
} from './locales';
import { DEFAULT_NAMESPACE, I18N_NAMESPACES, I18nNamespace } from './namespaces';
import {
  LocaleNamespaceResource,
  SOURCE_LOCALE_NAMESPACE_RESOURCES,
  HI_IN_NAMESPACE_RESOURCES,
  TE_IN_NAMESPACE_RESOURCES,
  ES_419_NAMESPACE_RESOURCES,
  DE_DE_NAMESPACE_RESOURCES,
} from './resourceManifest';
import { resolveInitialLocalePreferenceAsync } from './preferences';
import { pseudoTransformResource } from './pseudo';

type LocaleNamespaceResources = Record<I18nNamespace, LocaleNamespaceResource>;
type LocaleResourceLoader = () => Promise<LocaleNamespaceResources>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createEmptyLocaleNamespaceResources = (): LocaleNamespaceResources =>
  I18N_NAMESPACES.reduce((accumulator, namespace) => {
    accumulator[namespace] = {};
    return accumulator;
  }, {} as LocaleNamespaceResources);

// FT-265: Derive en-XA resources by pseudo-transforming every en-US string.
// This produces a fully translated pseudolocale with ~40% text expansion and
// accented characters so layout issues surface immediately during development.
const createPseudoLocaleNamespaceResources = (): LocaleNamespaceResources =>
  I18N_NAMESPACES.reduce((accumulator, namespace) => {
    accumulator[namespace] = pseudoTransformResource(
      SOURCE_LOCALE_NAMESPACE_RESOURCES[namespace]
    ) as LocaleNamespaceResource;
    return accumulator;
  }, {} as LocaleNamespaceResources);

const localeResourceLoaders: Record<AppLocale, LocaleResourceLoader> = {
  'en-US': async () => SOURCE_LOCALE_NAMESPACE_RESOURCES,
  'hi-IN': async () => HI_IN_NAMESPACE_RESOURCES,
  'te-IN': async () => TE_IN_NAMESPACE_RESOURCES,
  'es-419': async () => ES_419_NAMESPACE_RESOURCES,
  'de-DE': async () => DE_DE_NAMESPACE_RESOURCES,
  'en-XA': async () => createPseudoLocaleNamespaceResources(),
};

const loadedLocales = new Set<AppLocale>([SOURCE_LOCALE]);
let hasInitializedI18n = false;
const mutableI18n = i18n as any;

const applyDocumentLocale = (locale: AppLocale) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.lang = locale;
  document.documentElement.dir = LOCALE_REGISTRY[locale].direction;
};

export const loadLocaleResources = async (locale: AppLocale): Promise<LocaleNamespaceResources> => {
  const loader = localeResourceLoaders[locale];
  return loader();
};

export const ensureLocaleResources = async (locale: AppLocale): Promise<void> => {
  if (loadedLocales.has(locale)) {
    return;
  }

  const resources = await loadLocaleResources(locale);
  I18N_NAMESPACES.forEach((namespace) => {
    mutableI18n.addResourceBundle(locale, namespace, resources[namespace], true, true);
  });

  loadedLocales.add(locale);
};

export const initializeI18n = async (): Promise<typeof i18n> => {
  if (!hasInitializedI18n) {
    const initialLocale = await resolveInitialLocalePreferenceAsync();
    const initialResources: Record<string, LocaleNamespaceResources> = {
        [SOURCE_LOCALE]: SOURCE_LOCALE_NAMESPACE_RESOURCES,
    };

    if (initialLocale !== SOURCE_LOCALE) {
      initialResources[initialLocale] = await loadLocaleResources(initialLocale);
      loadedLocales.add(initialLocale);
    }

    await i18n
      .use(initReactI18next)
      .init({
        resources: initialResources,
        lng: initialLocale,
        fallbackLng: SOURCE_LOCALE,
        supportedLngs: [...PRODUCTION_LOCALES, PSEUDO_LOCALE],
        ns: [...I18N_NAMESPACES],
        defaultNS: DEFAULT_NAMESPACE,
        fallbackNS: DEFAULT_NAMESPACE,
        interpolation: {
          escapeValue: false,
        },
        returnNull: false,
        load: 'currentOnly',
        partialBundledLanguages: true,
      });

    hasInitializedI18n = true;
    mutableI18n.on('languageChanged', (language: string) => {
      applyDocumentLocale(resolveSupportedLocale(language));
    });

    // FT-267: In non-production builds, log a warning whenever i18next falls
    // back to the key name because no translation was found. This surfaces
    // missing keys immediately during development and QA without requiring a
    // separate build step.
    if (process.env.NODE_ENV !== 'production') {
      mutableI18n.on(
        'missingKey',
        (
          lngs: string | string[],
          namespace: string,
          key: string,
          _fallbackValue: string
        ) => {
          const locale = Array.isArray(lngs) ? lngs[0] : lngs;
          // Silence pseudo-locale fallbacks — en-XA intentionally falls through
          // to en-US for any key not yet transformed at startup.
          if (locale === PSEUDO_LOCALE) return;
          console.warn(
            `[i18n] Missing translation key: ${namespace}:${key} (locale: ${locale})`
          );
        }
      );
    }
  }

  applyDocumentLocale(resolveSupportedLocale(mutableI18n.language ?? mutableI18n.resolvedLanguage));
  return i18n;
};

export const changeAppLanguage = async (requestedLocale: string): Promise<AppLocale> => {
  const locale = resolveSupportedLocale(requestedLocale);
  await ensureLocaleResources(locale);
  await mutableI18n.changeLanguage(locale);
  applyDocumentLocale(locale);
  return locale;
};

export const getCurrentAppLocale = (): AppLocale =>
  resolveSupportedLocale(mutableI18n.language ?? mutableI18n.resolvedLanguage ?? DEFAULT_LOCALE);

export const isLocaleLoaded = (locale: AppLocale): boolean => loadedLocales.has(locale);

export { i18n };
